import { useId, type ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Field, FieldError, FieldLabel } from '../base/field'

export type FieldWrapperIds = {
  controlId: string
  errorId: string | undefined
}

export type FieldWrapperProps = {
  label?: ReactNode
  error?: string
  orientation?: 'vertical' | 'horizontal'
  /**
   * Overrides the generated control id. Controls whose id a Cypress selector
   * already targets pass their field name here so `htmlFor` keeps matching it.
   */
  controlId?: string
  /** Applied to the element wrapping the label, the control and the error. */
  className?: string
  /** Where the label sits relative to the control in the Field row. */
  labelPlacement?: 'start' | 'end'
  children: (ids: FieldWrapperIds) => ReactNode
}

// Renders the label and error text shared by every form control, and hands
// the control the ids it needs to wire up its own `aria-invalid` and
// `aria-describedby`. The error always sits below the label/control row,
// whichever orientation that row uses.
export function FieldWrapper({
  label,
  error,
  orientation = 'vertical',
  controlId: controlIdOverride,
  className,
  labelPlacement = 'start',
  children
}: FieldWrapperProps) {
  const baseId = useId()
  const controlId = controlIdOverride ?? `${baseId}-item`
  const errorId = error ? `${baseId}-message` : undefined
  const fieldLabel =
    label === undefined ? null : (
      <FieldLabel htmlFor={controlId}>{label}</FieldLabel>
    )

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Field orientation={orientation}>
        {labelPlacement === 'start' ? fieldLabel : null}
        {children({ controlId, errorId })}
        {labelPlacement === 'end' ? fieldLabel : null}
      </Field>
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  )
}
